import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";

// User-facing appearance preference. Persisted per browser (like the OS setting it mirrors),
// so it applies before login and is not tied to any one organization.
const THEME_MODE_KEY = 'mediagraph-theme-mode'
const THEME_MODES = ['light', 'dark', 'system']
const DEFAULT_THEME_MODE = 'light'
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'

const isThemeMode = value => THEME_MODES.includes(value)

// Even touching window.localStorage can throw (sandboxed/opaque origins), so resolve it inside the guard.
const defaultStorage = () => globalThis.localStorage

const readStoredThemeMode = (storage) => {
    try {
        const store = storage === undefined ? defaultStorage() : storage
        const stored = store?.getItem(THEME_MODE_KEY)
        return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE
    } catch {
        return DEFAULT_THEME_MODE
    }
}

const writeStoredThemeMode = (mode, storage) => {
    try {
        const store = storage === undefined ? defaultStorage() : storage
        store?.setItem(THEME_MODE_KEY, mode)
    } catch {
        // Private mode / quota / storage-denied: the in-memory preference still applies for this page.
    }
}

// 'system' follows the OS; anything unrecognized falls back to light.
const resolveThemeMode = (mode, systemPrefersDark) => {
    if(mode === 'system') return systemPrefersDark ? 'dark' : 'light'
    return mode === 'dark' ? 'dark' : 'light'
}

const getSystemPrefersDark = () =>
    typeof globalThis.matchMedia === 'function' && globalThis.matchMedia(DARK_SCHEME_QUERY).matches

// Mirrors the resolved theme onto <html> so plain CSS (theme.less) and native UI
// (scrollbars, form controls via color-scheme) follow antd's algorithm.
const applyThemeToDocument = resolvedTheme => {
    const root = globalThis.document?.documentElement
    if(!root) return
    root.dataset.theme = resolvedTheme
    root.style.colorScheme = resolvedTheme
}

const ThemeContext = createContext({
    mode: DEFAULT_THEME_MODE,
    resolvedTheme: 'light',
    isDark: false,
    setMode: () => {},
})

const ThemeProvider = ({children, initialMode}) => {
    const [mode, setModeState] = useState(() => isThemeMode(initialMode) ? initialMode : readStoredThemeMode())
    const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark)

    useEffect(() => {
        if(typeof globalThis.matchMedia !== 'function') return

        const media = globalThis.matchMedia(DARK_SCHEME_QUERY)
        const onChange = e => setSystemPrefersDark(e.matches)
        setSystemPrefersDark(media.matches)

        media.addEventListener?.('change', onChange)
        return () => media.removeEventListener?.('change', onChange)
    }, [])

    // Keep tabs in sync when the preference changes elsewhere.
    useEffect(() => {
        const onStorage = e => {
            if(e.key === THEME_MODE_KEY) setModeState(readStoredThemeMode())
        }
        globalThis.addEventListener?.('storage', onStorage)
        return () => globalThis.removeEventListener?.('storage', onStorage)
    }, [])

    const resolvedTheme = resolveThemeMode(mode, systemPrefersDark)

    useEffect(() => {
        applyThemeToDocument(resolvedTheme)
    }, [resolvedTheme])

    const setMode = useCallback(next => {
        if(!isThemeMode(next)) return
        writeStoredThemeMode(next)
        setModeState(next)
    }, [])

    const value = useMemo(() => ({
        mode,
        setMode,
        resolvedTheme,
        isDark: resolvedTheme === 'dark',
    }), [mode, setMode, resolvedTheme])

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

const useThemeMode = () => useContext(ThemeContext)

export {
    ThemeContext,
    ThemeProvider,
    useThemeMode,
    THEME_MODE_KEY,
    THEME_MODES,
    DEFAULT_THEME_MODE,
    readStoredThemeMode,
    resolveThemeMode,
    applyThemeToDocument,
}
